import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { API } from "@/App";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { AveragesCard, UserReviewCard, ReviewList } from "@/components/reviews/ReviewCards";

const DEFAULT_FORM = { food_rating: 5, service_rating: 5, ambiance_rating: 5, comment: "" };

const ReviewSection = ({ restaurant, userId }) => {
  const { t } = useTranslation();
  const restaurantId = restaurant?.restaurant_id;
  const [reviews, setReviews] = useState([]);
  const [averages, setAverages] = useState({ food: 0, service: 0, ambiance: 0, overall: 0 });
  const [userReview, setUserReview] = useState(null);
  const [isWriting, setIsWriting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurants/${restaurantId}/reviews`, { withCredentials: true });
      setReviews(response.data.reviews);
      setAverages(response.data.averages);
    } catch (error) {
      console.warn("Error fetching reviews:", error.message);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  const fetchUserReview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurants/${restaurantId}/reviews/user`, { withCredentials: true });
      if (response.data) {
        setUserReview(response.data);
        setFormData({
          food_rating: response.data.food_rating,
          service_rating: response.data.service_rating,
          ambiance_rating: response.data.ambiance_rating,
          comment: response.data.comment || ""
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Loading user review failed:", error);
      }
    }
  }, [restaurantId]);

  useEffect(() => {
    if (restaurantId) {
      fetchReviews();
      fetchUserReview();
    }
  }, [restaurantId, fetchReviews, fetchUserReview]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isEditing && userReview) {
        await axios.put(`${API}/reviews/${userReview.review_id}`, formData, { withCredentials: true });
        toast.success(t('reviews.reviewUpdated'));
      } else {
        await axios.post(
          `${API}/restaurants/${restaurantId}/reviews`,
          { ...formData, restaurant_id: restaurantId },
          { withCredentials: true }
        );
        toast.success(t('reviews.reviewSubmitted'));
      }
      setIsWriting(false);
      setIsEditing(false);
      fetchReviews();
      fetchUserReview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!userReview) return;
    try {
      await axios.delete(`${API}/reviews/${userReview.review_id}`, { withCredentials: true });
      toast.success(t('reviews.reviewDeleted'));
      setUserReview(null);
      setFormData(DEFAULT_FORM);
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setIsWriting(true);
  };

  const cancelWriting = () => {
    setIsWriting(false);
    setIsEditing(false);
    if (userReview) {
      setFormData({
        food_rating: userReview.food_rating,
        service_rating: userReview.service_rating,
        ambiance_rating: userReview.ambiance_rating,
        comment: userReview.comment || ""
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="review-section">
      {reviews.length > 0 && <AveragesCard averages={averages} reviewCount={reviews.length} />}

      {isWriting ? (
        <ReviewForm
          formData={formData}
          onFormChange={setFormData}
          isEditing={isEditing}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={cancelWriting}
        />
      ) : userReview ? (
        <UserReviewCard userReview={userReview} onEdit={startEditing} onDelete={handleDelete} />
      ) : (
        <Button
          onClick={() => setIsWriting(true)}
          variant="outline"
          className="w-full border-border hover:border-primary"
          data-testid="write-review-button"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          {t('reviews.writeReview')}
        </Button>
      )}

      <ReviewList reviews={reviews} userId={userId} />
    </div>
  );
};

export default ReviewSection;
